/**
 * Типизированный fetch-клиент для FastAPI.
 *
 * H4: токен живёт в HttpOnly cookie. Здесь только credentials:"include",
 * чтобы браузер сам прицепил cookie на same-origin запросы. Никаких
 * Authorization-заголовков из localStorage больше нет
 *
 * При 401 в dev-режиме пробуем один раз поднять сессию через
 * /api/dev/bootstrap-session (он тоже ставит cookie)
 */

export interface ApiError {
  code: string;
  status: number;
  message?: string;
}

export class ApiHttpError extends Error implements ApiError {
  code: string;
  status: number;
  constructor(code: string, status: number, message?: string) {
    super(message || code);
    this.code = code;
    this.status = status;
  }
}

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  _retry?: boolean;             // приватный флаг, не передавайте снаружи
}

function buildUrl(path: string, query?: RequestOptions["query"]): string {
  const url = path.startsWith("/api") ? path : `/api${path}`;
  if (!query) return url;
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null) usp.set(k, String(v));
  }
  const qs = usp.toString();
  return qs ? `${url}?${qs}` : url;
}

// Lazy-импорт чтобы избежать циклической зависимости api ↔ devAuth.
async function recoverFrom401(): Promise<boolean> {
  try {
    const { ensureDevSession } = await import("@/lib/devAuth");
    await ensureDevSession();
    return true;
  } catch {
    return false;
  }
}

export async function apiFetch<T = unknown>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const headers = new Headers(opts.headers || {});
  if (opts.body !== undefined && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const res = await fetch(buildUrl(path, opts.query), {
    ...opts,
    headers,
    // H4: cookie с JWT прицепляется автоматически на same-origin запросы
    credentials: "include",
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
  });

  if (!res.ok) {
    // 401-handler: пробуем восстановить dev-сессию один раз. Без token-state
    // нет смысла сравнивать «изменился ли токен» - просто пробуем dev
    // bootstrap (он в проде вернёт 404 и мы упадём как обычно)
    if (res.status === 401 && !opts._retry && !path.startsWith("/api/dev/") && !path.startsWith("/api/auth/")) {
      const ok = await recoverFrom401();
      if (ok) return apiFetch<T>(path, { ...opts, _retry: true });
    }

    let code = "http_error";
    let message: string | undefined;
    try {
      const data = await res.json();
      code = data.detail || data.code || code;
      message = data.message || (typeof data.detail === "string" ? data.detail : undefined);
    } catch {
      /* non-JSON body */
    }
    throw new ApiHttpError(code, res.status, message);
  }

  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  get:  <T>(p: string, q?: RequestOptions["query"]) => apiFetch<T>(p, { method: "GET", query: q }),
  post: <T>(p: string, body?: unknown) => apiFetch<T>(p, { method: "POST", body }),
  put:  <T>(p: string, body?: unknown) => apiFetch<T>(p, { method: "PUT",  body }),
  patch: <T>(p: string, body?: unknown) => apiFetch<T>(p, { method: "PATCH", body }),
  del:  <T>(p: string) => apiFetch<T>(p, { method: "DELETE" }),
};
